/**
 * section-image-carousel.js
 * ──────────────────────────
 * Adds mouse drag-to-scroll to every image carousel track on the page.
 * Touch/swipe is handled natively via overflow-x + scroll-snap.
 *
 * Snap strategy
 * ─────────────
 * CSS scroll-snap is disabled while the user drags (via [data-dragging]).
 * On release we programmatically smooth-scroll to the nearest snap point
 * and only re-enable CSS snap *after* that scroll settles — preventing the
 * jarring jump that happens when snap re-engages mid-animation.
 */
(function () {
  'use strict';

  /* ─── Snap to nearest slide after drag release ─────────────── */

  function snapToNearest(track) {
    var firstItem = track.querySelector('.img-carousel__item');
    if (!firstItem) {
      track.removeAttribute('data-dragging');
      return;
    }

    var gap    = parseFloat(getComputedStyle(track).gap) || 0;
    var stride = firstItem.offsetWidth + gap;
    if (stride <= 0) {
      track.removeAttribute('data-dragging');
      return;
    }

    var items        = track.querySelectorAll('.img-carousel__item');
    var nearestIndex = Math.round(track.scrollLeft / stride);
    nearestIndex     = Math.max(0, Math.min(nearestIndex, items.length - 1));
    var targetLeft   = nearestIndex * stride;

    /* If we're already sitting on the snap point (e.g. dragging past the
       last slide where scrollLeft is clamped), scrollTo won't move anything
       so scrollend will never fire — just remove data-dragging immediately. */
    if (Math.abs(track.scrollLeft - targetLeft) < 1) {
      track.removeAttribute('data-dragging');
      return;
    }

    /* Smooth-scroll to the snap point. CSS snap stays OFF (data-dragging
       remains) so it can't fight the animation mid-flight. */
    track.scrollTo({ left: targetLeft, behavior: 'smooth' });

    /* Re-enable CSS snap once the scroll has settled.
       scrollend is Chrome 114+ / Firefox 109+; fall back to a timeout. */
    if ('onscrollend' in window) {
      var onEnd = function () {
        track.removeEventListener('scrollend', onEnd);
        track.removeAttribute('data-dragging');
      };
      track.addEventListener('scrollend', onEnd);
    } else {
      setTimeout(function () {
        track.removeAttribute('data-dragging');
      }, 450);
    }
  }

  /* ─── Per-track drag handler ────────────────────────────────── */

  function initDragScroll(track) {
    var active          = false;
    var startX          = 0;
    var startScrollLeft = 0;
    var hasMoved        = false;

    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return; // left button only
      active          = true;
      hasMoved        = false;
      startX          = e.clientX;
      startScrollLeft = track.scrollLeft;
      track.setAttribute('data-dragging', '');
      // Prevent the browser's native image-drag behaviour
      e.preventDefault();
    });

    document.addEventListener('mousemove', function (e) {
      if (!active) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) hasMoved = true;
      track.scrollLeft = startScrollLeft - dx;
    });

    document.addEventListener('mouseup', function () {
      if (!active) return;
      active = false;

      if (hasMoved) {
        /* Snap smoothly — keeps data-dragging on until scroll settles */
        snapToNearest(track);
      } else {
        /* Plain click — re-enable snap immediately */
        requestAnimationFrame(function () {
          track.removeAttribute('data-dragging');
        });
      }
    });

    /* Swallow the click that fires after a drag so image links aren't
       accidentally followed when the user was just scrolling. */
    track.addEventListener(
      'click',
      function (e) {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          hasMoved = false;
        }
      },
      true // capture phase — fires before any child click handlers
    );
  }

  /* ─── Autoplay ──────────────────────────────────────────────── */

  function initAutoplay(track) {
    /* Honour the user's motion preference — no autoplay if reduced motion */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

    var items = track.querySelectorAll('.img-carousel__item');
    if (items.length <= 1) return null;

    var timer  = null;
    var section = track.closest('.img-carousel') || track;

    function getStride() {
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      return items[0].offsetWidth + gap;
    }

    function advance() {
      var stride  = getStride();
      if (stride <= 0) return;

      var current = Math.round(track.scrollLeft / stride);
      var next    = current + 1;

      if (next >= items.length) {
        /* At the last slide — jump instantly back to the start so the
           transition isn't a long backwards scroll across all slides */
        track.scrollTo({ left: 0, behavior: 'instant' });
      } else {
        track.scrollTo({ left: next * stride, behavior: 'smooth' });
      }
    }

    function start() {
      if (timer) return;
      timer = setInterval(advance, 4000);
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    /* Read paused state directly from the button's aria-pressed attribute
       so there's a single source of truth — no separate variable to get out of sync */
    function isUserPaused() {
      var btn = section.querySelector('[data-carousel-pause]');
      return btn !== null && btn.getAttribute('aria-pressed') === 'true';
    }

    /* Pause while the cursor is anywhere over the section */
    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', function () {
      if (!isUserPaused()) start();
    });

    /* Pause during touch interactions; resume after the snap settles.
       Check isUserPaused() inside the callback — not before scheduling —
       so the click event (which fires ~300ms after touchend on mobile)
       has time to update aria-pressed before we decide whether to restart. */
    section.addEventListener('touchstart', stop, { passive: true });
    section.addEventListener('touchend', function () {
      setTimeout(function () {
        if (!isUserPaused()) start();
      }, 600);
    });

    start();

    return { start: start, stop: stop };
  }

  /* ─── Prev / Next / Pause controls ─────────────────────────── */

  function initControls(track, autoplay) {
    var section    = track.closest('.img-carousel');
    if (!section) return;

    var items      = track.querySelectorAll('.img-carousel__item');
    var total      = items.length;
    var statusEl   = section.querySelector('[data-carousel-status]');
    var prevBtn    = section.querySelector('[data-carousel-prev]');
    var nextBtn    = section.querySelector('[data-carousel-next]');
    var pauseBtn   = section.querySelector('[data-carousel-pause]');

    function getStride() {
      var gap = parseFloat(getComputedStyle(track).gap) || 0;
      return items[0] ? items[0].offsetWidth + gap : 0;
    }

    function getCurrentIndex() {
      var stride = getStride();
      return stride > 0 ? Math.round(track.scrollLeft / stride) : 0;
    }

    function goToIndex(index) {
      var stride = getStride();
      if (stride <= 0) return;
      index = Math.max(0, Math.min(index, total - 1));
      track.scrollTo({ left: index * stride, behavior: 'smooth' });
      if (statusEl) statusEl.textContent = 'Slide ' + (index + 1) + ' of ' + total;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goToIndex(getCurrentIndex() - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goToIndex(getCurrentIndex() + 1);
      });
    }

    if (pauseBtn) {
      /* Hide the pause button entirely when autoplay is disabled
         (e.g. prefers-reduced-motion) */
      if (!autoplay) {
        pauseBtn.hidden = true;
      } else {
        pauseBtn.addEventListener('click', function () {
          var isPaused = pauseBtn.getAttribute('aria-pressed') === 'true';
          if (isPaused) {
            autoplay.start();
            pauseBtn.setAttribute('aria-pressed', 'false');
            pauseBtn.setAttribute('aria-label', 'Pause slideshow');
          } else {
            autoplay.stop();
            pauseBtn.setAttribute('aria-pressed', 'true');
            pauseBtn.setAttribute('aria-label', 'Resume slideshow');
          }
        });
      }
    }
  }

  /* ─── Init ──────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('[data-carousel-track]').forEach(function (track) {
      initDragScroll(track);
      var autoplay = initAutoplay(track);
      initControls(track, autoplay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
